import React, { useEffect, useState } from "react";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Navbar from "../components/Navbar.js";
import styles from "../styles/Opportunities.module.css";
import modalStyles from "../styles/Modals.module.css";
import axios from 'axios';
import PageTransition from "../components/Animation/PageTransition.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthProvider.js";
import Title from '../components/ui/Title';


export default function Opportunity() {

  const API_BASE = process.env.REACT_APP_API_URL;
  const LOCAL_BASE = "http://localhost:3001"

  const location = useLocation();
  const openEventId = location.state?.eventId;
  const nav = useNavigate();

  const { auth } = useAuth();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [dateFromFilter, setDateFromFilter] = useState(null);
  const [dateToFilter, setDateToFilter] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const [confirmModal, setConfirmModal] = useState(false);
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [alreadySignedUp, setAlreadySignedUp] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");
  const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    fontFamily: '"Gill Sans MT", "Gill Sans", Calibri, sans-serif',
    backgroundColor: "#fff",
    borderRadius: "8px",
    borderColor: state.isFocused ? "#7494ec" : "#cfd8f7",
    boxShadow: state.isFocused
      ? "0 0 0 3px rgba(116,148,236,0.25)"
      : "0 2px 8px rgba(116,148,236,0.08)",
    padding: "2px",
    transition: "all 0.2s ease",
    "&:hover": { borderColor: "#7494ec" },
    transform: state.isFocused ? "translateY(-1px)" : "translateY(0)",
    "&:hover": {
      borderColor: "#7494ec",
      transform: "translateY(-1px)",
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: "#64748b",
    fontSize: "0.95rem",
    fontFamily: '"Gill Sans MT", "Gill Sans", Calibri, sans-serif',
  }),
  singleValue: (base) => ({
  ...base,
  color: "#0f172a",
  fontWeight: 500,
  fontFamily: '"Gill Sans MT", "Gill Sans", Calibri, sans-serif',
  whiteSpace: "nowrap",        // Prevent line breaks inside the value
  overflow: "visible",         // Make sure text doesn't get clipped
  textOverflow: "unset",       // Disable ellipsis
  minWidth: "2.5ch",           // Ensure enough min width for very short words
}),

  option: (base, state) => ({
    ...base,
    fontFamily: '"Gill Sans MT", "Gill Sans", Calibri, sans-serif',
    backgroundColor: state.isFocused ? "#eef2ff" : "#fff",
    color: "#0f172a",
    cursor: "pointer",
    padding: "10px 12px",
    transition: "background 0.15s ease",
    borderRadius: "0", // ensures highlight fills full width
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? "#7494ec" : "#64748b",
    transition: "color 0.2s",
    "&:hover": { color: "#7494ec" },
  }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({
    ...base,
    borderRadius: "8px",
    marginTop: "4px",
    boxShadow: "0 8px 20px rgba(15,23,42,0.15)",
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};



  useEffect(() => {
    const fetchOpportunities = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE}/events/get_all_published_events`);
        const data = Array.isArray(res.data.result) ? res.data.result : [];
        const normalized = data.map((it) => {
          const get = (...keys) => {
            for (const k of keys) if (it[k] != null) return it[k];
            return undefined;
          };
          if (it.registration_count === it.capacity) return undefined;
          const toNumber = (v) => (v == null || v === "" ? null : (Number.isNaN(Number(v)) ? null : Number(v)));
          return {
            event_id: get("event_id", "id"),
            org_name: get("org_name"),
            title: get("title") || "Untitled opportunity",
            description: get("description") || "",
            location: get("location") || "",
            capacity: toNumber(get("capacity")) || null,
            start_date: get("start_date", "date") || null,
            end_date: get("end_date", "date") || null,
            start_time: get("start_time") || null,
            end_time: get("end_time") || null,
            hours: toNumber(get("hours")) || null,
            status: get("status") || "pending",
            category: get("category") || "general",
            region: get("region") || null,
            image_url: get("image_url") || null,
            registration_count: get("registration_count"),
          };
        }).filter(Boolean);

        setOpportunities(normalized);
      } catch (err) {
        console.error("❌ Failed to load opportunities", err);
        setOpportunities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOpportunities();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API_BASE}/events/get_all_categories`);
        setCategories(Array.isArray(res.data.result) ? res.data.result : []);
      } catch (err) { console.error("Error loading categories", err); }
    };
    const fetchRegions = async () => {
      try {
        const res = await axios.get(`${API_BASE}/events/get_all_regions`);
        setRegions(Array.isArray(res.data.result) ? res.data.result : []);
      } catch (err) { console.error("Error loading regions", err); }
    };

    fetchCategories();
    fetchRegions();
  }, []);

  useEffect(() => {
    if (openEventId && opportunities.length > 0) {
      const match = opportunities.find(op => op.event_id === openEventId);
      if (match) {
        setSelectedOpportunity(match);
        setShowModal(true);
      }
    }
  }, [openEventId, opportunities])

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {  // show button after scrolling 300px
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSignUp = async () => {
    if (auth.token.length < 1) {
      nav("/volunteer/auth")
      return;
    }
    if (auth.role === "organiser") {
      setErrorModalMessage("Only Volunteers can sign up!");
      setShowErrorModal(true);
      return;
    }
    setConfirmModal(true);
  }

  const confirmSignUp = async () => {
    if (!selectedOpportunity || !auth.id) return;
    try {
      setConfirmModal(false);
      setSignUpLoading(true);

      const userSignedUp = await axios.get(`${API_BASE}/events/check_if_user_signed_up`, {params: {user_id: auth.id, event_id: selectedOpportunity.event_id}});

      if (userSignedUp.data.result.length > 0) {
        setAlreadySignedUp(true);
      } else {
        const res = await axios.post(`${API_BASE}/events/signup_event`, {user_id: auth.id, event_id: selectedOpportunity.event_id, })

        if (res.data.result) {
          setSignUpSuccess(true);
        } else {
          setErrorModalMessage("Something went wrong.");
          setShowErrorModal(true);
        }
      }
      setSignUpLoading(false);
    } catch (err) {
      console.error(err);
      setErrorModalMessage("Error occurred while signing up.");
      setShowErrorModal(true);
      setSignUpLoading(false);
    }
  }

  // Close any open modals and return to the Opportunities list view
  const handleAcknowledgement = () => {
    setSignUpSuccess(false);
    setAlreadySignedUp(false);
    setConfirmModal(false);
    setShowModal(false);
    setSelectedOpportunity(null);
    // Clear any navigation state so modal doesn't auto-open again
    nav('/opportunity', { replace: true, state: {} });
  };

  const resetFilters = () => {
    setCategoryFilter("");
    setRegionFilter("");
    setDateFromFilter(null);
    setDateToFilter(null);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="container py-4 flex-grow-1" style={{ position: 'relative', minHeight: '50vh' }}>
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 50,
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
          >
            <div className="text-center">
              <div className="spinner-border text-primary" role="status" style={{ marginBottom: '12px' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mb-0">Loading opportunities...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (opportunities.length === 0) {
    return (
      <>
        <Navbar />
        <div className={styles['no-opportunities-message']}>
          <p>No opportunities found.</p>
        </div>
      </>
    );
  }

  const filteredOpportunities = opportunities
    .filter((op) => (categoryFilter ? op.category === categoryFilter : true))
    .filter((op) => (regionFilter ? op.region === regionFilter : true))
    .filter((op) => (dateFromFilter ? new Date(op.start_date) >= dateFromFilter : true))
    .filter((op) => (dateToFilter ? new Date(op.start_date) <= dateToFilter : true));

  return (
    <>

      <Navbar />
      <PageTransition>
        <div className="container py-4 flex-grow-1">
        
          <Title text="Opportunity" subtitle="Sign up for exciting opportunities to give back to your community!"/>


          <div className={styles.filters}>
            <div className={styles['group-left']}>
              <div className={styles['date-filter']}>
      {/* CATEGORY FILTER */}
      <Select
        value={
          categoryFilter
            ? { value: categoryFilter, label: categoryFilter }
            : null
        }
        onChange={(opt) => setCategoryFilter(opt?.value || "")}
        options={[
          { value: "", label: "All Categories" },
          ...categories.map((catObj) => ({
            value: catObj.category ?? catObj.name ?? "",
            label: catObj.category ?? catObj.name ?? "Unknown",
          })),
        ]}
        placeholder="All Categories"
        styles={customSelectStyles}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />

      {/* REGION FILTER */}
      <Select
        value={
          regionFilter
            ? { value: regionFilter, label: regionFilter }
            : null
        }
        onChange={(opt) => setRegionFilter(opt?.value || "")}
        options={[
          { value: "", label: "All Regions" },
          ...regions.map((regObj) => ({
            value: regObj.region ?? regObj.name ?? "",
            label: regObj.region ?? regObj.name ?? "Unknown",
          })),
        ]}
        placeholder="All Regions"
        styles={customSelectStyles}
        menuPortalTarget={document.body}
        menuPosition="fixed"
      />

            </div>
          </div>
            

            <div className={styles['group-right']}>
              <div className={styles['date-filter']}>
                <label>
                  <span className={styles['date-label']}>Start</span>
                  <DatePicker
                    selected={dateFromFilter}
                    onChange={(date) => setDateFromFilter(date)}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select start date"
                    className={styles['custom-datepicker']}
                    isClearable
                  />
                </label>
                <label>
                  <span className={styles['date-label']}>End</span>
                  <DatePicker
                    selected={dateToFilter}
                    onChange={(date) => setDateToFilter(date)}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select end date"
                    className={styles['custom-datepicker']}
                    isClearable
                    minDate={dateFromFilter}
                  />
                </label>
              </div>
              <button className={styles['reset-btn']} onClick={resetFilters}>
                <i className="bi bi-arrow-counterclockwise" style={{ marginRight: "6px" }}></i>
                Reset
              </button>
            </div>
          </div>

          <div className={styles['card-grid']}>
            {filteredOpportunities.length === 0 ? (
              <div className={styles['empty-state-card']}>
                <div className={styles['empty-icon']}></div>
                <h3>No opportunities found</h3>
                <p>Try adjusting your filters or reset to see all available opportunities.</p>
              </div>
            ) : (
              filteredOpportunities.map((op) => (
                <div className={styles['event-card']} key={op.event_id} onClick={() => { setShowModal(true); setSelectedOpportunity(op); }}>
                  <div className={styles['card-image']}>
                    <img src={op.image_url || '/images/default-opportunity.svg'} alt={op.title} />
                    {/* <span className={`${styles['status-badge']} ${styles[op.status]}`}>{op.status}</span> */}
                  </div>

                  <div className={styles['card-content']}>
                    <h2 className={styles['event-title']}>{op.title}</h2>
                    <p className="text-muted">{op.org_name}</p>
                    <p>{op.description}</p>
                    <div className={styles['card-info']}>
                      <div className={styles['info-item']}>
                        <span className={styles['info-icon']} aria-hidden="true">📍</span>
                        <span className={styles['info-text']}>{op.location || "N/A"}</span>
                      </div>

                      <div className={styles['info-item']}>
                        <span className={styles['info-icon']} aria-hidden="true">📅</span>
                        <span className={styles['info-text']}>
                          {formatDate(op.start_date)} - {formatTime(op.start_time)} - {formatTime(op.end_time)}
                        </span>
                      </div>

                      <div className={styles['info-item']}>
                        <span className={styles['info-icon']} aria-hidden="true">👤</span>
                        <span className={styles['info-text']}>Capacity: {op.capacity ?? "N/A"}</span>
                      </div>

                    </div>
                  </div>

                  <hr className={styles['card-divider']} />

                  <div className={styles['card-footer']}>
                    <span className={`${styles['category-tag']} ${styles[`category-${op.category?.toLowerCase().replace(/\s+/g, "-")}`] || styles['category-general']}`}>
                      {op.category ?? "Uncategorized"}
                    </span>
                    <button className={styles['signup-btn']}>Sign Up</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        

        {showModal && selectedOpportunity && (
          <>
            <div className={styles["modal-overlay"]} onClick={() => setShowModal(false)}>
              <div className={styles["modal-wrapper"]} onClick={(e) => e.stopPropagation()}>
                <button className={styles["modal-close"]} onClick={() => setShowModal(false)}>
                  &times;
                </button>

                <div className={styles["modal-content-row"]}>
                  <div className={styles["modal-left"]}>
                    <img
                      src={selectedOpportunity.image_url || '/images/default-opportunity.svg'}
                      alt={selectedOpportunity.title}
                      className={styles["modal-large-image"]}
                    />
                  </div>

                  <div className={styles["modal-right"]}>
                    <h2 className={styles["modal-title"]}>{selectedOpportunity.title}</h2>
                    <p className={styles["modal-desc"]}>{selectedOpportunity.description}</p>

                    <div className={styles["modal-detail-list"]}>
                      <p>
                        <span aria-hidden="true" style={{ marginRight: "6px" }}>📍</span>
                        {selectedOpportunity.location || "N/A"}
                      </p>
                      <p>
                        <span aria-hidden="true" style={{ marginRight: "6px" }}>📅</span>
                        {formatDate(selectedOpportunity.start_date)}{" "}
                        {formatTime(selectedOpportunity.start_time)} –{" "}
                        {formatTime(selectedOpportunity.end_time)}
                      </p>
                      <p>
                        <span aria-hidden="true" style={{ marginRight: "6px" }}>👤</span> Capacity:{" "}
                        {selectedOpportunity.capacity ?? "N/A"}
                      </p>
                      <p>Category: {selectedOpportunity.category}</p>
                      <p>Region: {selectedOpportunity.region}</p>
                    </div>

                    <button className={styles["modal-signup-btn"]} onClick={handleSignUp}>
                      Sign Up
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {confirmModal && (
          <div className={styles["modal-overlay"]} onClick={() => setConfirmModal(false)}>
            <div className={styles["confirm-modal"]} onClick={(e) => e.stopPropagation()}>
              <h3>Confirm Sign Up</h3>
              <p>Are you sure you want to sign up for <b>{selectedOpportunity.title}</b>?</p>
              <div className={styles["confirm-buttons"]}>
                <button onClick={() => setConfirmModal(false)} className={styles["cancel-btn"]}>Cancel</button>
                <button onClick={confirmSignUp} className={styles["confirm-btn"]}>Yes, Sign Me Up</button>
              </div>
            </div>
          </div>
        )}

        {signUpLoading && (
          <div className={styles["modal-overlay"]}>
            <div className={styles["loading-modal"]}>
              <div className={styles["spinner"]}></div>
              <p>Submitting your application...</p>
            </div>
          </div>
        )}

        {signUpSuccess && (
          <div className={styles.overlay} onClick={() => { setSignUpSuccess(false); handleAcknowledgement(); }}>
            <div className={styles.dialog} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
              <div className={styles.icon} style={{ color: 'green' }}>✓</div>
              <div className={styles.title}>Sign Up Successful!</div>
              <div className={styles.body}>Your application is pending organiser confirmation.</div>
              <div className={styles.buttons}>
                <button className={styles.btnPrimary} onClick={handleAcknowledgement}>OK</button>
              </div>
            </div>
          </div>
        )}

        {alreadySignedUp && (
          <div className={styles.overlay} onClick={handleAcknowledgement}>
            <div className={styles.dialog} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
              <div className={styles.icon} style={{ color: 'red' }}>⚠️</div>
              <div className={styles.title}>Sign Up Failed</div>
              <div className={styles.body}>You have already signed up for this event!</div>
              <div className={styles.buttons}>
                <button className={styles.btnPrimary} onClick={handleAcknowledgement}>OK</button>
              </div>
            </div>
          </div>
        )}

        {showErrorModal && (
          <div className={modalStyles.overlay} onClick={() => setShowErrorModal(false)}>
            <div className={modalStyles.dialog} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
              <div className={modalStyles.icon} style={{ color: '#f59e0b' }}>⚠️</div>
              <div className={modalStyles.title}>Error</div>
              <div className={modalStyles.body}>{errorModalMessage}</div>
              <div className={modalStyles.buttons}>
                <button className={modalStyles.btnPrimary} onClick={() => setShowErrorModal(false)}>OK</button>
              </div>
            </div>
          </div>
        )}

        {showScrollTop && (
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className={styles.scrollTopButton}
            aria-label="Scroll to top"
          >
            ↑
          </button>
        )}

      </PageTransition>
    </>
  );
}

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-SG", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatTime = (timeStr) => {
  if (!timeStr) return "";
  const [hours, minutes] = timeStr.split(":");
  const date = new Date();
  date.setHours(Number(hours), Number(minutes));
  return date.toLocaleTimeString("en-SG", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};
